import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import { find, map, mergeRight, propEq } from "ramda";
import { caseInsensitiveSort } from "../../util/sorts";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import makeStyles from "@mui/styles/makeStyles";
import ReadOnlyField from "../../components/ReadOnlyField";
import { useOrganizations } from "../../user-admin/userServices";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ProjectEmailDialog from "../edit/ProjectEmailDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const ProjectEmailCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setEditDialogOpen(false);
      console.log(data);
      const update = mergeRight(project, data);
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const [organizations, setOrganizations] = useState([]);
  const organizationsResponse = useOrganizations();
  useEffect(() => {
    if (
      organizationsResponse.isSuccess &&
      notBlank(organizationsResponse.data)
    ) {
      setOrganizations(organizationsResponse.data);
    }
  }, [organizationsResponse.isSuccess, organizationsResponse.data]);

  const findOrganizationName = useCallback(
    orgId => {
      const organization = find(propEq("organizationId", orgId), organizations);
      return notBlank(organization) ? organization.organizationName : null;
    },
    [organizations],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Email"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Edit Email Properties">
              <IconButton onClick={handleEditClick} size="large">
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Project Email Properties.
          </Box>
        )}
        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={8}>
              <Grid container spacing={3}>
                <Grid item sm={6}>
                  <LabeledReadOnly label="Email Template">
                    <ReadOnlyField>{project.emailTemplate}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Email From Address (optional)">
                    <ReadOnlyField>{project.emailFromAddress}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Email Subject">
                    <ReadOnlyField>{project.emailSubject}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Manager Email Subject (optional)">
                    <ReadOnlyField>{project.managerEmailSubject}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={4}>
              <LabeledReadOnly label="Manager Email Addresses">
                {notBlank(project.projectManagerEmails) ? (
                  <List dense>
                    {map(
                      managerEmail => (
                        <ListItem
                          key={
                            managerEmail.projectManagerEmailId ||
                            managerEmail.emailAddres
                          }
                        >
                          <ListItemText
                            primary={managerEmail.emailAddress}
                            secondary={findOrganizationName(
                              managerEmail.organizationId,
                            )}
                          />
                        </ListItem>
                      ),
                      caseInsensitiveSort("emailAddress")(
                        project.projectManagerEmails,
                      ),
                    )}
                  </List>
                ) : (
                  <Typography>--</Typography>
                )}
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <ProjectEmailDialog
          open={editDialogOpen}
          project={project}
          onCancel={handleEditCancel}
          onApply={handleEditApply}
        />
      )}
    </Card>
  );
};

export default ProjectEmailCard;
